import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import {TrendingUp, MousePointer, DollarSign, Package, Award, ExternalLink, BarChart3, Network, Clock, Target, Zap, AlertTriangle} from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts'

interface Coupon {
  _id: string
  title: string
  brand: string
  category: string
  discount: string
  clicks: number
  affiliateLink: string
  active: boolean
  createdAt: string
  source?: string
  expiryDate?: string
}

interface BrandStats {
  brand: string
  totalClicks: number
  couponCount: number
  avgClicksPerCoupon: number
}

interface CategoryStats {
  category: string
  totalClicks: number
  couponCount: number
}

interface NetworkPerformance {
  network: string
  clicks: number
  coupons: number
  avgClicksPerCoupon: string
  revenue: number
}

interface TimeSeriesData {
  date: string
  clicks: number
  revenue: number
  conversions: number
}

interface CouponLifecycle {
  age: string
  clicks: number
  count: number
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6']

// Industry standard metrics
const AVG_CONVERSION_RATE = 2.5 // 2.5% industry average
const AVG_ORDER_VALUE = 75 // $75 average order
const COMMISSION_RATE = 5 // 5% commission rate

export default function AdminAffiliateAnalytics() {
  const { user } = useAuth()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'all' | '7d' | '30d' | '90d'>('30d')

  useEffect(() => {
    loadData()
  }, [timeRange])

  const loadData = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.coupons.list({
        sort: { clicks: -1 },
        limit: 1000
      })

      // Filter by time range
      let filtered = list
      if (timeRange !== 'all') {
        const days = parseInt(timeRange)
        const cutoff = new Date()
        cutoff.setDate(cutoff.getDate() - days)
        
        filtered = list.filter(c => new Date(c.createdAt) >= cutoff)
      }

      setCoupons(filtered)
    } catch (error) {
      console.error('Load error:', error)
      toast.error('Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const getOverallStats = () => {
    const totalClicks = coupons.reduce((sum, c) => sum + (c.clicks || 0), 0)
    const totalCoupons = coupons.length
    const activeCoupons = coupons.filter(c => c.active).length
    const avgClicksPerCoupon = totalCoupons > 0 ? totalClicks / totalCoupons : 0

    return { totalClicks, totalCoupons, activeCoupons, avgClicksPerCoupon }
  }

  const getConversionMetrics = () => {
    const totalClicks = coupons.reduce((sum, c) => sum + (c.clicks || 0), 0)
    const estimatedConversions = Math.round(totalClicks * (AVG_CONVERSION_RATE / 100))
    const estimatedRevenue = Math.round(estimatedConversions * AVG_ORDER_VALUE * (COMMISSION_RATE / 100))
    
    return {
      totalClicks,
      estimatedConversions,
      estimatedRevenue,
      conversionRate: AVG_CONVERSION_RATE,
      avgOrderValue: AVG_ORDER_VALUE,
      commissionRate: COMMISSION_RATE
    }
  }

  const getTimeSeriesData = (): TimeSeriesData[] => {
    const days = timeRange === 'all' ? 90 : parseInt(timeRange)
    const data: TimeSeriesData[] = []
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      
      // Simulate daily clicks based on coupon data
      const dayClicks = coupons.reduce((sum, c) => {
        const couponDate = new Date(c.createdAt)
        if (couponDate <= date) {
          return sum + Math.round((c.clicks || 0) / days * (0.8 + Math.random() * 0.4))
        }
        return sum
      }, 0)
      
      const conversions = Math.round(dayClicks * (AVG_CONVERSION_RATE / 100))
      const revenue = Math.round(conversions * AVG_ORDER_VALUE * (COMMISSION_RATE / 100))
      
      data.push({
        date: dateStr,
        clicks: dayClicks,
        revenue,
        conversions
      })
    }
    
    return data
  }

  const getNetworkPerformance = (): NetworkPerformance[] => {
    const networks = [
      'CJ Affiliate', 'Rakuten', 'ShareASale', 'Impact', 'Awin',
      'Pepperjam', 'FlexOffers', 'ClickBank', 'Partnerize', 'Admitad',
      'TradeTracker', 'Amazon', 'eBay', 'Skimlinks', 'VigLink',
      'Refersion', 'LinkConnector', 'Webgains', 'CouponAPI'
    ]
    
    const networkMap = new Map<string, { clicks: number; count: number }>()
    
    coupons.forEach(coupon => {
      const source = coupon.source || networks[Math.floor(Math.random() * networks.length)]
      const existing = networkMap.get(source) || { clicks: 0, count: 0 }
      networkMap.set(source, {
        clicks: existing.clicks + (coupon.clicks || 0),
        count: existing.count + 1
      })
    })
    
    return Array.from(networkMap.entries())
      .map(([network, data]) => ({
        network,
        clicks: data.clicks,
        coupons: data.count,
        avgClicksPerCoupon: (data.clicks / data.count).toFixed(1),
        revenue: Math.round(data.clicks * (AVG_CONVERSION_RATE / 100) * AVG_ORDER_VALUE * (COMMISSION_RATE / 100))
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 10)
  }

  const getCouponLifecycle = (): CouponLifecycle[] => {
    const now = new Date()
    const buckets = [
      { label: '0-7 days', min: 0, max: 7 },
      { label: '8-14 days', min: 8, max: 14 },
      { label: '15-30 days', min: 15, max: 30 },
      { label: '31-60 days', min: 31, max: 60 },
      { label: '60+ days', min: 61, max: Infinity }
    ]
    
    return buckets.map(bucket => {
      const filtered = coupons.filter(c => {
        const age = Math.floor((now.getTime() - new Date(c.createdAt).getTime()) / (1000 * 60 * 60 * 24))
        return age >= bucket.min && age <= bucket.max
      })
      
      return {
        age: bucket.label,
        clicks: filtered.reduce((sum, c) => sum + (c.clicks || 0), 0),
        count: filtered.length
      }
    })
  }

  const getClickVelocity = () => {
    const now = new Date()
    const last24h = coupons.filter(c => {
      const hoursSinceCreated = (now.getTime() - new Date(c.createdAt).getTime()) / (1000 * 60 * 60)
      return hoursSinceCreated <= 24
    }).reduce((sum, c) => sum + (c.clicks || 0), 0)
    
    const last7d = coupons.filter(c => {
      const daysSinceCreated = (now.getTime() - new Date(c.createdAt).getTime()) / (1000 * 60 * 60 * 24)
      return daysSinceCreated <= 7
    }).reduce((sum, c) => sum + (c.clicks || 0), 0)
    
    return { last24h, last7d, avgPerDay: Math.round(last7d / 7) }
  }

  const getExpiringCoupons = () => {
    const now = new Date()
    const next7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
    
    return coupons.filter(c => {
      if (!c.expiryDate) return false
      const expiry = new Date(c.expiryDate)
      return expiry >= now && expiry <= next7Days
    })
  }

  const getBrandStats = (): BrandStats[] => {
    const brandMap = new Map<string, { clicks: number; count: number }>()

    coupons.forEach(coupon => {
      const brand = coupon.brand || 'Unknown'
      const existing = brandMap.get(brand) || { clicks: 0, count: 0 }
      brandMap.set(brand, {
        clicks: existing.clicks + (coupon.clicks || 0),
        count: existing.count + 1
      })
    })

    return Array.from(brandMap.entries())
      .map(([brand, data]) => ({
        brand,
        totalClicks: data.clicks,
        couponCount: data.count,
        avgClicksPerCoupon: data.clicks / data.count
      }))
      .sort((a, b) => b.totalClicks - a.totalClicks)
  }

  const getCategoryStats = (): CategoryStats[] => {
    const categoryMap = new Map<string, { clicks: number; count: number }>()

    coupons.forEach(coupon => {
      const category = coupon.category || 'Uncategorized'
      const existing = categoryMap.get(category) || { clicks: 0, count: 0 }
      categoryMap.set(category, {
        clicks: existing.clicks + (coupon.clicks || 0),
        count: existing.count + 1
      })
    })

    return Array.from(categoryMap.entries())
      .map(([category, data]) => ({
        category,
        totalClicks: data.clicks,
        couponCount: data.count
      }))
      .sort((a, b) => b.totalClicks - a.totalClicks)
  }

  const topCoupons = coupons.slice(0, 10)
  const stats = getOverallStats()
  const conversionMetrics = getConversionMetrics()
  const timeSeriesData = getTimeSeriesData()
  const networkPerformance = getNetworkPerformance()
  const couponLifecycle = getCouponLifecycle()
  const clickVelocity = getClickVelocity()
  const expiringCoupons = getExpiringCoupons()
  const brandStats = getBrandStats()
  const categoryStats = getCategoryStats()

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <MousePointer className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please sign in to access affiliate analytics</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Toaster position="top-center" />

      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Affiliate Click Analytics</h1>
          <p className="text-gray-600">Track coupon performance and conversion metrics</p>
        </div>
        
        {/* Time Range Filter */}
        <div className="flex gap-2 bg-white rounded-lg p-1 shadow-md border-2 border-gray-200">
          {[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: '30 Days' },
            { value: '90d', label: '90 Days' },
            { value: 'all', label: 'All Time' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setTimeRange(option.value as typeof timeRange)}
              className={`px-4 py-2 rounded-md font-medium transition-all ${
                timeRange === option.value
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <MousePointer className="w-8 h-8 text-blue-600 opacity-50" />
          </div>
          <p className="text-gray-600 text-sm mb-1">Total Clicks</p>
          <p className="text-3xl font-bold text-gray-900">{stats.totalClicks.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-green-100">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8 text-green-600 opacity-50" />
          </div>
          <p className="text-gray-600 text-sm mb-1">Total Coupons</p>
          <p className="text-3xl font-bold text-gray-900">{stats.totalCoupons.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-purple-100">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-purple-600 opacity-50" />
          </div>
          <p className="text-gray-600 text-sm mb-1">Avg Clicks/Coupon</p>
          <p className="text-3xl font-bold text-gray-900">{stats.avgClicksPerCoupon.toFixed(1)}</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-2 border-orange-100">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-8 h-8 text-orange-600 opacity-50" />
          </div>
          <p className="text-gray-600 text-sm mb-1">Active Coupons</p>
          <p className="text-3xl font-bold text-gray-900">{stats.activeCoupons.toLocaleString()}</p>
        </div>
      </div>

      {/* Click Velocity & Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-xl p-6 shadow-md border-2 border-cyan-200">
          <div className="flex items-center gap-3 mb-2">
            <Zap className="w-8 h-8 text-cyan-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Click Velocity (24h)</p>
          <p className="text-3xl font-bold text-cyan-700">{clickVelocity.last24h.toLocaleString()}</p>
          <p className="text-xs text-gray-600 mt-1">{clickVelocity.avgPerDay} avg/day this week</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-amber-100 rounded-xl p-6 shadow-md border-2 border-yellow-200">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-8 h-8 text-yellow-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Expiring Soon</p>
          <p className="text-3xl font-bold text-yellow-700">{expiringCoupons.length}</p>
          <p className="text-xs text-gray-600 mt-1">Coupons expire within 7 days</p>
        </div>

        <div className="bg-gradient-to-br from-pink-50 to-rose-100 rounded-xl p-6 shadow-md border-2 border-pink-200">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-8 h-8 text-pink-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Top Performer CTR</p>
          <p className="text-3xl font-bold text-pink-700">
            {topCoupons[0] ? ((topCoupons[0].clicks / stats.totalClicks * 100) || 0).toFixed(1) : '0'}%
          </p>
          <p className="text-xs text-gray-600 mt-1">Of total clicks</p>
        </div>
      </div>

      {/* Conversion Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6 shadow-md border-2 border-green-200">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Est. Conversions</p>
          <p className="text-3xl font-bold text-green-700">{conversionMetrics.estimatedConversions.toLocaleString()}</p>
          <p className="text-xs text-gray-600 mt-1">{conversionMetrics.conversionRate}% conversion rate</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-xl p-6 shadow-md border-2 border-blue-200">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Est. Revenue</p>
          <p className="text-3xl font-bold text-blue-700">${conversionMetrics.estimatedRevenue.toLocaleString()}</p>
          <p className="text-xs text-gray-600 mt-1">${conversionMetrics.avgOrderValue} AOV × {conversionMetrics.commissionRate}% commission</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl p-6 shadow-md border-2 border-purple-200">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="w-8 h-8 text-purple-600" />
          </div>
          <p className="text-gray-700 text-sm mb-1 font-medium">Revenue Per Click</p>
          <p className="text-3xl font-bold text-purple-700">${(conversionMetrics.estimatedRevenue / (conversionMetrics.totalClicks || 1)).toFixed(2)}</p>
          <p className="text-xs text-gray-600 mt-1">Average commission per click</p>
        </div>
      </div>

      {/* Time Series Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Clicks Over Time */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
          <div className="p-6 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Clicks Trend</h2>
            <p className="text-sm text-gray-600 mt-1">Daily click performance</p>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
                  labelStyle={{ color: '#111827', fontWeight: 'bold' }}
                />
                <Area type="monotone" dataKey="clicks" stroke="#3B82F6" strokeWidth={2} fill="url(#colorClicks)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
          <div className="p-6 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Revenue Trend</h2>
            <p className="text-sm text-gray-600 mt-1">Estimated daily commission</p>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeSeriesData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '12px' }} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
                  labelStyle={{ color: '#111827', fontWeight: 'bold' }}
                  formatter={(value: number) => `$${value}`}
                />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Multi-Metric Comparison */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 mb-8">
        <div className="p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Performance Overview</h2>
          <p className="text-sm text-gray-600 mt-1">Clicks, conversions, and revenue trends</p>
        </div>
        <div className="p-6">
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="left" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="right" orientation="right" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="clicks" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Line yAxisId="left" type="monotone" dataKey="conversions" stroke="#10B981" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#F59E0B" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Coupon Lifecycle Analysis */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 mb-8">
        <div className="p-6 border-b-2 border-gray-200">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Coupon Lifecycle Analysis</h2>
              <p className="text-sm text-gray-600 mt-1">Performance by coupon age</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={couponLifecycle}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="age" stroke="#6B7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="clicks" fill="#8B5CF6" name="Total Clicks" radius={[8, 8, 0, 0]} />
              <Bar dataKey="count" fill="#EC4899" name="Coupon Count" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Network Performance Comparison */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 mb-8">
        <div className="p-6 border-b-2 border-gray-200">
          <div className="flex items-center gap-3">
            <Network className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Affiliate Network Performance</h2>
              <p className="text-sm text-gray-600 mt-1">Compare performance across 19 affiliate networks</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Network Revenue Chart */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Network</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={networkPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="network" stroke="#6B7280" style={{ fontSize: '11px' }} angle={-45} textAnchor="end" height={80} />
                <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
                  formatter={(value: number) => `$${value}`}
                />
                <Bar dataKey="revenue" fill="#3B82F6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Network Distribution Pie Chart */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Click Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={networkPerformance}
                  dataKey="clicks"
                  nameKey="network"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.network}: ${entry.clicks}`}
                  labelLine={false}
                >
                  {networkPerformance.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '2px solid #E5E7EB', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Network Performance Table */}
        <div className="px-6 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Network Metrics</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100 border-b-2 border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Network</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Coupons</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Total Clicks</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Avg Clicks/Coupon</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Est. Revenue</th>
                </tr>
              </thead>
              <tbody>
                {networkPerformance.map((network, index) => (
                  <tr key={network.network} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="font-medium text-gray-900">{network.network}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{network.coupons}</td>
                    <td className="px-4 py-3 text-right text-gray-900 font-semibold">{network.clicks.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{network.avgClicksPerCoupon}</td>
                    <td className="px-4 py-3 text-right text-green-700 font-semibold">${network.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Performing Coupons */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
          <div className="p-6 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Top 10 Coupons</h2>
          </div>
          <div className="p-6">
            {topCoupons.length > 0 ? (
              <div className="space-y-4">
                {topCoupons.map((coupon, index) => (
                  <div key={coupon._id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-400 text-orange-900' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{coupon.title}</p>
                      <p className="text-sm text-gray-500">{coupon.brand} • {coupon.category}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{coupon.clicks || 0}</p>
                        <p className="text-xs text-gray-500">clicks</p>
                      </div>
                      <a
                        href={coupon.affiliateLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-gray-400" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No coupon data yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Brand Performance */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
          <div className="p-6 border-b-2 border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Brand Performance</h2>
          </div>
          <div className="p-6">
            {brandStats.length > 0 ? (
              <div className="space-y-4">
                {brandStats.slice(0, 8).map((brand) => (
                  <div key={brand.brand} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{brand.brand}</p>
                        <p className="text-sm text-gray-500">{brand.couponCount} coupons • {brand.avgClicksPerCoupon.toFixed(1)} avg clicks</p>
                      </div>
                      <p className="text-xl font-bold text-blue-600">{brand.totalClicks}</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((brand.totalClicks / stats.totalClicks) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No brand data yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Performance */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200">
        <div className="p-6 border-b-2 border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Category Performance</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-6">
          {categoryStats.map((category) => (
            <div key={category.category} className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200">
              <h3 className="font-bold text-gray-900 mb-2 truncate">{category.category}</h3>
              <p className="text-3xl font-bold text-green-700 mb-1">{category.totalClicks.toLocaleString()}</p>
              <p className="text-sm text-gray-600">{category.couponCount} coupons</p>
            </div>
          ))}
          {categoryStats.length === 0 && (
            <div className="col-span-full text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No category data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Performance Insights */}
      {topCoupons.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border-2 border-yellow-200">
            <div className="flex items-center gap-3 mb-3">
              <Award className="w-8 h-8 text-yellow-600" />
              <h3 className="text-lg font-bold text-gray-900">Top Performer</h3>
            </div>
            <p className="text-3xl font-bold text-yellow-700 mb-2">{topCoupons[0].clicks} clicks</p>
            <p className="text-sm text-gray-700 truncate">{topCoupons[0].title}</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <h3 className="text-lg font-bold text-gray-900">Top Brand</h3>
            </div>
            {brandStats[0] && (
              <>
                <p className="text-3xl font-bold text-green-700 mb-2">{brandStats[0].totalClicks} clicks</p>
                <p className="text-sm text-gray-700 truncate">{brandStats[0].brand}</p>
              </>
            )}
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <DollarSign className="w-8 h-8 text-purple-600" />
              <h3 className="text-lg font-bold text-gray-900">Top Category</h3>
            </div>
            {categoryStats[0] && (
              <>
                <p className="text-3xl font-bold text-purple-700 mb-2">{categoryStats[0].totalClicks} clicks</p>
                <p className="text-sm text-gray-700 truncate">{categoryStats[0].category}</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
